import Link from "next/link";
import React, { useState } from "react";
import { MoreHorizontal, Eye, EyeOff } from "react-feather";
import { CourseItem } from "../../../types";
import { pluralize } from "../../../utils";
import { Badge } from "../Badge";
import { DropdownMenu } from "../DropdownMenu";
import { IconButton } from "../IconButton";
import { DeleteCourseDialog } from "./DeleteCourseDialog";
import { EditCourseDialog } from "./EditCourseDialog";
import { ManageClinicsDialog } from "./ManageClinicsDialog";
import { useUpdateCourseVisibility } from "../../../data/mutations/useUpdateCourseVisibility";
import { toast } from "react-toastify";

type CourseCardProps = {
  course: CourseItem;
  canManage: boolean;
};

export const CourseCard = ({ course, canManage }: CourseCardProps) => {
  const [isDropdownMenuOpen, setIsDropdownMenuOpen] = useState(false);
  const [isEditCourseDialogOpen, setIsEditCourseDialogOpen] = useState(false);
  const [isDeleteCourseDialogOpen, setIsDeleteCourseDialogOpen] = useState(false);
  const [isManageClinicsDialogOpen, setIsManageClinicsDialogOpen] = useState(false);

  const [updateCourseVisibility] = useUpdateCourseVisibility();

  const handleSaveCourseVisibility = async (courseId: string, clinicIds: string[]) => {
    try {
      await updateCourseVisibility({
        variables: {
          courseId,
          clinicIds,
        },
      });
    } catch (error) {
      toast("Failed to update course visibility", { type: "error" });
      throw error;
    }
  };

  return (
    <>
      <Link href={`/courses/${course.slug}`} key={course.id} passHref legacyBehavior>
        <a
          className={
            "flex cursor-pointer flex-col rounded-md border-[1px] border-gray-300 bg-gray-50 p-4 transition duration-standard ease-in-out hover:border-gray-400 hover:bg-white"
          }>
          <div className={"flex items-center justify-between"}>
            <div className={"flex items-center justify-center gap-3"}>
              <h2 className="leading-0 text-lg">{course.name}</h2>
              <Badge>{pluralize(course.sessions.data.length, "Session")}</Badge>

              {course.status === "draft" ? <Badge>{course.status.toUpperCase()}</Badge> : null}
            </div>

            {canManage && (
              <DropdownMenu.DropdownMenu
                open={isDropdownMenuOpen}
                onOpenChange={setIsDropdownMenuOpen}>
                <DropdownMenu.Trigger>
                  <IconButton>
                    <MoreHorizontal />
                  </IconButton>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content>
                  <DropdownMenu.Item onSelect={() => setIsEditCourseDialogOpen(true)}>
                    Edit
                  </DropdownMenu.Item>
                  <DropdownMenu.Item onSelect={() => setIsManageClinicsDialogOpen(true)}>
                    Manage Visibility
                  </DropdownMenu.Item>
                  <DropdownMenu.Item onSelect={() => setIsDeleteCourseDialogOpen(true)}>
                    Delete
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.DropdownMenu>
            )}
          </div>
          <div className={"pt-3"}>
            <p className={"text-sm leading-tight text-gray-600"}>{course.description}</p>
            {canManage && (
              <div className={"flex items-center gap-2 mt-2"}>
                {course.visibleToClinics.length > 0 ? (
                  <Eye size={14} className="text-gray-500" />
                ) : (
                  <EyeOff size={14} className="text-gray-500" />
                )}
                <div className={"flex flex-wrap gap-1"}>
                  {course.visibleToClinics.length > 0 ? (
                    course.visibleToClinics.map((clinic) => (
                      <Badge key={clinic.id}>{clinic.name}</Badge>
                    ))
                  ) : (
                    <Badge className="bg-yellow-100 border-yellow-200 text-yellow-800">
                      Not visible to any clinics
                    </Badge>
                  )}
                </div>
              </div>
            )}
          </div>
        </a>
      </Link>

      {canManage && (
        <EditCourseDialog
          course={course}
          open={isEditCourseDialogOpen}
          onOpenChange={setIsEditCourseDialogOpen}
        />
      )}

      {canManage && (
        <DeleteCourseDialog
          course={course}
          open={isDeleteCourseDialogOpen}
          onOpenChange={setIsDeleteCourseDialogOpen}
        />
      )}

      {canManage && (
        <ManageClinicsDialog
          item={course}
          itemType="course"
          open={isManageClinicsDialogOpen}
          onOpenChange={setIsManageClinicsDialogOpen}
          onSave={handleSaveCourseVisibility}
        />
      )}
    </>
  );
};
